/**
 * /ingest — Project ingestion (Developer Hub). Surface Layer (/01)
 *
 * Operator-facing ingestion lane: pick a client project, attribute the upload
 * to a workspace member, drop documents, and enqueue one `knowledge.ingest_files`
 * ActionFrame per file so the agent organizes + embeds them into that project's
 * RAG namespace. Real upload/storage is pending — today each file is dispatched
 * by name; non-text files are flagged for the Vision/OCR lane downstream.
 *
 * Reads projects (`GET /api/projects`) and the signed-in identity's memberships
 * (`GET /api/me`) for attribution. All writes flow through the dispatch store.
 */

import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  Button,
  Column,
  Dropdown,
  FileUploaderDropContainer,
  FileUploaderItem,
  Grid,
  InlineNotification,
  SkeletonText,
  Tag,
  TextInput,
  Tile,
} from '@carbon/react';
import { DocumentAdd } from '@carbon/icons-react';
import type { ProjectRow } from '@contracts/runtime-endpoints.contract';
import { useMe, useProjects } from '@lib/persistence/useRuntimeData';
import { useDispatchStore } from '../rebuilt/action-dispatch';
import '../styles/views.scss';

export const Route = createFileRoute('/ingest')({
  component: IngestView,
});

const ACCEPTED = ['.md', '.txt', '.pdf', '.json', '.csv'];
const TEXT_EXTENSIONS = ['.md', '.txt', '.json', '.csv'];

interface ProjectItem {
  id: string;
  label: string;
  slug: string;
  ragNamespace: string | null;
}

interface IngestResult {
  fileName: string;
  detail: string;
  visionLane: boolean;
}

/** Minimal structural shape of an uploaded file (only `.name` is used). Avoids the
 *  DOM `File` global so the strict lint stays clean; Carbon passes real File objects. */
type PickedFile = { name: string };

function isTextFile(fileName: string): boolean {
  const lower = fileName.toLowerCase();
  return TEXT_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

function IngestView() {
  const dispatch = useDispatchStore((s) => s.dispatch);
  const { projects, isLoading, tenantContextMissing } = useProjects();
  const { memberships, me } = useMe();

  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [results, setResults] = useState<IngestResult[]>([]);
  const [busy, setBusy] = useState(false);

  const projectItems: ProjectItem[] = projects.map((project: ProjectRow) => ({
    id: project.id,
    label: project.display_label,
    slug: project.slug,
    ragNamespace: project.rag_namespace,
  }));

  const memberItems = memberships.map((member) => member.tenantId);
  const hasMembers = memberItems.length > 0;
  const attribution = hasMembers ? selectedUser : userEmail.trim() || me?.email || null;

  function addFiles(incoming: PickedFile[]) {
    setFiles((current) => {
      const seen = new Set(current.map((file) => file.name));
      const next = incoming.filter((file) => !seen.has(file.name));
      return [...current, ...next];
    });
  }

  function removeFile(name: string) {
    setFiles((current) => current.filter((file) => file.name !== name));
  }

  async function ingest() {
    if (!selectedProject || files.length === 0) return;
    setBusy(true);
    const collected: IngestResult[] = [];
    for (const file of files) {
      const textLane = isTextFile(file.name);
      const entry = await dispatch(
        'knowledge.ingest_files',
        {
          project: selectedProject.slug,
          path: file.name,
          user: attribution,
          rag_namespace: selectedProject.ragNamespace,
          lane: textLane ? 'embed' : 'vision_ocr',
        },
        { label: 'Ingest documents' }
      );
      collected.push({ fileName: file.name, detail: entry.detail, visionLane: !textLane });
    }
    setResults((current) => [...collected, ...current]);
    setFiles([]);
    setBusy(false);
  }

  const hasNonTextSelected = files.some((file) => !isTextFile(file.name));
  const canIngest = Boolean(selectedProject) && files.length > 0 && !busy;

  return (
    <div className="view">
      <header className="view__header">
        <h1 className="view__title">Project ingestion</h1>
        <p className="view__subtitle">
          Upload documents to a client project. The agent organizes and embeds each file into the
          RAG namespace of that project; non-text files route to the Vision/OCR lane first.
        </p>
      </header>

      <div className="surface-grid">
        <Grid condensed>
          <Column sm={4} md={8} lg={16}>
            <Tile className="view-tile">
              <h2 className="view-tile__value">New ingestion</h2>

              <Dropdown
                id="ingest-project"
                titleText="Project"
                helperText="Documents embed into the selected project's RAG namespace."
                label="Select a project"
                items={projectItems}
                selectedItem={selectedProject}
                itemToString={(item: ProjectItem | null) => (item ? item.label : '')}
                onChange={({ selectedItem }: { selectedItem: ProjectItem | null }) =>
                  setSelectedProject(selectedItem ?? null)
                }
              />
              {selectedProject ? (
                <div className="view-tile__row">
                  <span className="view-tile__label">RAG namespace</span>
                  <Tag type={selectedProject.ragNamespace ? 'teal' : 'cool-gray'}>
                    {selectedProject.ragNamespace ?? 'pending provisioning'}
                  </Tag>
                </div>
              ) : null}

              {hasMembers ? (
                <Dropdown
                  id="ingest-user"
                  titleText="Attribute to member"
                  helperText="The workspace member credited with this ingestion."
                  label="Select a member"
                  items={memberItems}
                  selectedItem={selectedUser}
                  itemToString={(item: string | null) => item ?? ''}
                  onChange={({ selectedItem }: { selectedItem: string | null }) =>
                    setSelectedUser(selectedItem ?? null)
                  }
                />
              ) : (
                <TextInput
                  id="ingest-user-email"
                  labelText="Attribute to (email)"
                  helperText="No memberships found — attribute this ingestion to an email."
                  placeholder="user@example.com"
                  value={userEmail}
                  onChange={(event) => setUserEmail(event.currentTarget.value)}
                />
              )}

              <div className="view-stack">
                <FileUploaderDropContainer
                  labelText="Drag and drop files here, or click to upload"
                  accept={ACCEPTED}
                  multiple
                  onAddFiles={(_event, { addedFiles }: { addedFiles: PickedFile[] }) =>
                    addFiles(addedFiles)
                  }
                />
                {files.map((file) => (
                  <FileUploaderItem
                    key={file.name}
                    uuid={file.name}
                    name={file.name}
                    status="edit"
                    iconDescription="Remove file"
                    onDelete={() => removeFile(file.name)}
                  />
                ))}
              </div>

              {hasNonTextSelected ? (
                <InlineNotification
                  kind="info"
                  lowContrast
                  hideCloseButton
                  title="Vision/OCR lane"
                  subtitle="PDF and other non-text files are routed to the Vision/OCR lane before embedding."
                />
              ) : null}

              <div className="view-actions">
                <Button renderIcon={DocumentAdd} disabled={!canIngest} onClick={() => void ingest()}>
                  Ingest
                </Button>
              </div>

              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                title="Storage pending"
                subtitle="File transfer is not yet wired — each file is dispatched by name as a knowledge.ingest_files ActionFrame."
              />
            </Tile>
          </Column>

          {tenantContextMissing ? (
            <Column sm={4} md={8} lg={16}>
              <InlineNotification
                kind="info"
                lowContrast
                hideCloseButton
                title="Workspace being provisioned"
                subtitle="Projects appear once your workspace context is ready."
              />
            </Column>
          ) : isLoading && projects.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <SkeletonText paragraph lineCount={4} />
            </Column>
          ) : projects.length === 0 ? (
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-empty">
                <h2>No projects yet</h2>
                <p className="view-tile__label">
                  Create a project before ingesting documents into its RAG.
                </p>
              </Tile>
            </Column>
          ) : null}

          {results.length > 0 ? (
            <Column sm={4} md={8} lg={16}>
              <Tile className="view-tile">
                <h2 className="view-tile__value">Dispatched files</h2>
                <div className="view-stack">
                  {results.map((result, index) => (
                    <div key={`${result.fileName}-${index}`} className="view-tile__row">
                      <strong>{result.fileName}</strong>
                      <Tag type={result.visionLane ? 'purple' : 'green'}>
                        {result.visionLane ? 'vision/ocr' : 'embed'}
                      </Tag>
                      <span className="notification-row__meta">{result.detail}</span>
                    </div>
                  ))}
                </div>
              </Tile>
            </Column>
          ) : null}
        </Grid>
      </div>
    </div>
  );
}
